let CameraHandler = function (___settings, ___handlers) {
  const THREE = require('../../../../../externals/three'),
        ExpoTHREE = ___settings.externalLibraries.ExpoTHREE,
        AR = ___settings.externalLibraries.AR,
        GLOBAL_UTILS = require('../../../../../shared/util/GlobalUtils'),
        CameraHandlerInterface = require('../../../../interfaces/handlers/CameraHandlerInterface'),
        _settings = ___settings.settings,
        _container = ___settings.container,
        _geometryNode = ___settings.geometryNode,
        _handlers = ___handlers;


  let that,
      _camera,
      _orbitControls, _orbitControlsProxy,
      _active = true;

      
  /**
   * Sets the FOV of the camera.
   *
   * @param {Number} value The new FOV value
   */
  let _fovHook = function (value) {
    if (!GLOBAL_UTILS.typeCheck(value, 'notnegative', _handlers.threeDManager.warn, 'CameraHandler.Hook->fov')) return false;

    if (_camera.fov > 10 && value < 10) {
      _camera.near *= 100 * value;
      _camera.far *= 100 * value;
    } else if (_camera.fov < 10 && value > 10) {
      _camera.near /= 100 * _camera.fov;
      _camera.far /= 100 * _camera.fov;
    }
    _camera.fov = value;

    _camera.updateProjectionMatrix();
    return true;
  };

  /**
   * Sets the perspective camera.
   *
   * @param {Object} value The new position and targer
   * @return {Promise<Boolean>} Resolves once the camera has been set to the requested loaction
   */
  let _perspectiveHook = function (value) {
    let scope = 'CameraHandler.Hook->defaults.perspective';
    if (!(GLOBAL_UTILS.typeCheck(value.position, 'vector3obj', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.target, 'vector3obj', _handlers.threeDManager.warn, scope)))
      return Promise.resolve(false);
    // Alex to Michael: Here we keep updating the camera when the default configuration gets changed,
    // because this hook is used by the SettingsHandler only (not available in API v2)
    return _camera instanceof THREE.PerspectiveCamera ?
      that.setPositionAndTarget(value.position, value.target, { duration: _settings.getSetting('cameraMovementDuration') }) :
      Promise.resolve(true);
  };

  /**
   * Sets the camera positions and targets.
   *
   * @param {Object} value the positions and targets of the cameras
   * @return {Promise<Boolean>} Resolves to true once the camera has been set to the requested location
   */
  let _defaultsHook = function (value) {
    let scope = 'CameraHandler.Hook->defaults';
    if (!value.perspective ||
      !(GLOBAL_UTILS.typeCheck(value.perspective.position, 'vector3obj', _handlers.threeDManager.warn, scope) &&
        GLOBAL_UTILS.typeCheck(value.perspective.target, 'vector3obj', _handlers.threeDManager.warn, scope)))
      return Promise.resolve(false);
    return Promise.resolve(true);
  };

  ////////////
  ////////////
  //
  // CameraHandler API - Orbit Control Settings
  //
  ////////////
  ////////////

  /**
   * Toggles the orbit controls.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _enableOrbitControlsHook = function (toggle) {
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', _handlers.threeDManager.warn, 'CameraHandler.Hook->enableOrbitControls')) return false;

    _orbitControls.enabled = toggle;
    return true;
  };

  ////////////
  ////////////
  //
  // CameraHandler API - Orbit Control Damping
  //
  ////////////
  ////////////

  /**
   * Sets the damping of the orbit controls.
   *
   * @param {Number} value The new damping value
   */
  let _dampingHook = function (value) {
    if (!GLOBAL_UTILS.typeCheck(value, 'notnegative', _handlers.threeDManager.warn, 'CameraHandler.Hook->damping')) return false;

    value = Math.min(Math.max(0.05, value), 0.25);
    _orbitControls.dampingFactor = value;
    _orbitControls.panDampingFactor = value;
    _orbitControls.zoomDampingFactor = value;
    _orbitControls.rotateDampingFactor = value;
    return true;
  };

  ////////////
  ////////////
  //
  // CameraHandler API - Orbit Control Rotation
  //
  ////////////
  ////////////

  /**
   * Toggles the possibility to rotate.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _enableRotationHook = function (toggle) {
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', _handlers.threeDManager.warn, 'CameraHandler.Hook->enableRotation')) return false;

    _orbitControls.enableRotation = toggle;
    return true;
  };

  /**
   * Sets the rotation speed of the orbit controls.
   *
   * @param {Number} value The new rotation value
   */
  let _rotationSpeedHook = function (value) {
    if (!GLOBAL_UTILS.typeCheck(value, 'float', _handlers.threeDManager.warn, 'CameraHandler.Hook->rotationSpeed')) return false;

    _orbitControls.rotationSpeed = value;
    return true;
  };

  ////////////
  ////////////
  //
  // CameraHandler API - Orbit Control Auto Rotation
  //
  ////////////
  ////////////

  /**
   * Toggles the auto rotation.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _enableAutoRotationHook = function (toggle) {
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', _handlers.threeDManager.warn, 'CameraHandler.Hook->enableAutoRotation')) return false;

    _orbitControls.enableAutoRotation = toggle;
    return true;
  };

  /**
   * Sets the auto rotation speed of the orbit controls.
   *
   * @param {Number} value The new rotation value
   */
  let _autoRotationSpeedHook = function (value) {
    if (!GLOBAL_UTILS.typeCheck(value, 'float', _handlers.threeDManager.warn, 'CameraHandler.Hook->autoRotationSpeed')) return false;

    _orbitControls.autoRotationSpeed = value;
    if (value !== 0) {
      _settings.updateSetting('enableAutoRotation', true);
    } else {
      _settings.updateSetting('enableAutoRotation', false);
    }

    return true;
  };

  ////////////
  ////////////
  //
  // CameraHandler API - Orbit Control Zoom
  //
  ////////////
  ////////////

  /**
   * Toggles the possibility to zoom.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _enableZoomHook = function (toggle) {
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', _handlers.threeDManager.warn, 'CameraHandler.Hook->enableZoom')) return false;

    _orbitControls.enableZoom = toggle;
    return true;
  };

  /**
   * Sets the zoom speed of the orbit controls.
   *
   * @param {Number} value The new zoom speed value
   */
  let _zoomSpeedHook = function (value) {
    if (!GLOBAL_UTILS.typeCheck(value, 'notnegative', _handlers.threeDManager.warn, 'CameraHandler.Hook->zoomSpeed')) return false;

    _orbitControls.zoomSpeed = value;
    return true;
  };

  ////////////
  ////////////
  //
  // CameraHandler API - Orbit Control Pan
  //
  ////////////
  ////////////

  /**
   * Toggles the possibility to pan.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _enablePanHook = function (toggle) {
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', _handlers.threeDManager.warn, 'CameraHandler.Hook->enablePan')) return false;

    _orbitControls.enablePan = toggle;
    return true;
  };

  /**
   * Sets the pan speed of the orbit controls.
   *
   * @param {Number} value The new pan speed value
   */
  let _panSpeedHook = function (value) {
    if (!GLOBAL_UTILS.typeCheck(value, 'number', _handlers.threeDManager.warn, 'CameraHandler.Hook->panSpeed')) return false;

    _orbitControls.panSpeed = value;
    return true;
  };

  ////////////
  ////////////
  //
  // CameraHandler API - Orbit Control Key Pan
  //
  ////////////
  ////////////

  /**
   * Toggles the possibility to pan with keys.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _enableKeyPanHook = function (toggle) {
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', _handlers.threeDManager.warn, 'CameraHandler.Hook->enableKeyPan')) return false;

    _orbitControls.enableKeyPan = toggle;
    return true;
  };

  /**
   * Sets the key pan speed of the orbit controls.
   *
   * @param {Number} value The new key pan speed value
   */
  let _keyPanSpeedHook = function (value) {
    if (!GLOBAL_UTILS.typeCheck(value, 'number', _handlers.threeDManager.warn, 'CameraHandler.Hook->keyPanSpeed')) return false;

    _orbitControls.keyPanSpeed = value * 7;
    return true;
  };

  ////////////
  ////////////
  //
  // CameraHandler API - Orbit Control Restrictions
  //
  ////////////
  ////////////

  /**
   * Restricts the camera position with two vectors.
   * These vectors can be seen as the minimum and the maximum of a cube.
   *
   * @param {THREE.Vector3} minPosition The minimum of the cube
   * @param {THREE.Vector3} maxPosition The maximum of the cube
   */
  let _restrictionsPositionCubeHook = function (value) {
    // TODO, restrictions testing
    let scope = 'CameraHandler.Hook->restrictions.position.cube';
    if (!(GLOBAL_UTILS.typeCheck(value.min, 'vector3any', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.max, 'vector3any', _handlers.threeDManager.warn, scope)))
      return false;
    _orbitControls.minPosition = GLOBAL_UTILS.toVector3(value.min);
    _orbitControls.maxPosition = GLOBAL_UTILS.toVector3(value.max);
    return true;
  };


  /**
   * Restricts the position of the camera with a sphere.
   *
   * @param {THREE.Vector3} position The center of the sphere
   * @param {Number} radius The radius of the sphere
   */
  let _restrictionsPositionSphereHook = function (value) {
    // TODO, restrictions testing
    let scope = 'CameraHandler.Hook->restrictions.position.sphere';
    if (!(GLOBAL_UTILS.typeCheck(value.center, 'vector3any', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.radius, 'notnegative', _handlers.threeDManager.warn, scope)))
      return false;

    _orbitControls.positionSphereCenter = GLOBAL_UTILS.toVector3(value.center);
    _orbitControls.positionSphereRadius = value.radius;
    return true;
  };


  /**
   * Restrcts the panning and therefore the camera target with two vectors.
   * These vectors can be seen as the minimum and the maximum of a cube.
   *
   * @param {THREE.Vector3} minTarget The minimum of the cube
   * @param {THREE.Vector3} maxTarget The maximum of the cube
   */
  let _restrictionsTargetCubeHook = function (value) {
    // TODO, restrictions testing
    let scope = 'CameraHandler.Hook->restrictions.target.cube';
    if (!(GLOBAL_UTILS.typeCheck(value.min, 'vector3any', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.max, 'vector3any', _handlers.threeDManager.warn, scope)))
      return false;

    _orbitControls.minTarget = GLOBAL_UTILS.toVector3(value.min);
    _orbitControls.maxTarget = GLOBAL_UTILS.toVector3(value.max);
    return true;
  };


  /**
   * Restricts the panning and therefore the camera target with a sphere.
   *
   * @param {THREE.Vector3} position The center of the sphere
   * @param {Number} radius The radius of the sphere
   */
  let _restrictionsTargetSphereHook = function (value) {
    // TODO, restrictions testing
    let scope = 'CameraHandler.Hook->restrictions.target.sphere';
    if (!(GLOBAL_UTILS.typeCheck(value.center, 'vector3any', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.radius, 'notnegative', _handlers.threeDManager.warn, scope)))
      return false;

    _orbitControls.targetSphereCenter = GLOBAL_UTILS.toVector3(value.center);
    _orbitControls.targetSphereRadius = value.radius;
    return true;
  };


  /**
   * Restricts the rotation with restrictions to the two angles.
   *
   * Polar Angle:
   *  from 0 to 180° (PI), where 180° is looking from the bottom up and 0 from the top down
   * Azimuth Angle:
   *  from -180° to 180°, horizontal rotation
   *
   * @param {Number} minPolarAngle
   * @param {Number} maxPolarAngle
   * @param {Number} minAzimuthAngle
   * @param {Number} maxAzimuthAngle
   */
  let _restrictionsRotationHook = function (value) {
    // TODO, restrictions testing
    let scope = 'CameraHandler.Hook->restrictions.rotation';
    if (!(GLOBAL_UTILS.typeCheck(value.minPolarAngle, 'number', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.maxPolarAngle, 'number', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.minAzimuthAngle, 'number', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.maxAzimuthAngle, 'number', _handlers.threeDManager.warn, scope)))
      return false;

    _orbitControls.minPolarAngle = value.minPolarAngle * Math.PI / 180.0;
    _orbitControls.maxPolarAngle = value.maxPolarAngle * Math.PI / 180.0;
    _orbitControls.minAzimuthAngle = value.minAzimuthAngle * Math.PI / 180.0;
    _orbitControls.maxAzimuthAngle = value.maxAzimuthAngle * Math.PI / 180.0;
    return true;
  };

  /**
   * Restricts the zoom with a minimum and maximum distance.
   *
   * @param {Object} value - The restrictions object
   * @param {Number} value.minDistance - The minimum distance
   * @param {Number} value.maxDistance - The maximum distance
   */
  let _restrictionsZoomHook = function (value) {
    // TODO, restrictions testing
    let scope = 'CameraHandler.Hook->restrictions.zoom';
    if (!(GLOBAL_UTILS.typeCheck(value.minDistance, 'number', _handlers.threeDManager.warn, scope) &&
      GLOBAL_UTILS.typeCheck(value.maxDistance, 'number', _handlers.threeDManager.warn, scope)))
      return false;

    _orbitControls.minDistance = value.minDistance;
    _orbitControls.maxDistance = value.maxDistance;
    return true;
  };

  /**
   * Notification when the factor for the zoom extents was changed.
   *
   * @param {Number} value - The factor
   */
  let _zoomExtentsFactorNotifier = function (/*value*/) {
    that.zoomExtents();
  };


  class CameraHandler extends CameraHandlerInterface {
    constructor() {
      super();
      that = this;

      _camera = new THREE.PerspectiveCamera(45, _container.width / _container.height, 0.01, 1000);
      let p = _settings.getSetting('defaults.perspective.position');
      _camera.position.set(p.x, p.y, p.z);
      _camera.up.set(0, 0, 1);
      _handlers.threeDManager.helpers.addSceneObject(_camera);

      _orbitControlsProxy = new (require('../helpers/OrbitControlsProxyExpoImplementation'))(_handlers);
      /**
       * The camera controls. We currently use orbit controls.
       * @private
       */
      _orbitControls = new THREE.OrbitControls(_camera, _orbitControlsProxy);

      // set the event listeners
      _orbitControlsProxy.initialize();

      // initialize with the default settings
      _orbitControls.enableDamping = true;
      _orbitControls.dampingFactor = _settings.getSetting('damping');
      _orbitControls.panDampingFactor = _settings.getSetting('damping');
      _orbitControls.zoomDampingFactor = _settings.getSetting('damping');
      _orbitControls.rotateDampingFactor = _settings.getSetting('damping');

      _orbitControls.enableRotation = _settings.getSetting('enableRotation');
      _orbitControls.rotationSpeed = _settings.getSetting('rotationSpeed');

      _orbitControls.enableAutoRotation = _settings.getSetting('enableAutoRotation');
      _orbitControls.autoRotationSpeed = _settings.getSetting('autoRotationSpeed');

      _orbitControls.enableZoom = _settings.getSetting('enableZoom');
      _orbitControls.zoomSpeed = _settings.getSetting('zoomSpeed');

      _orbitControls.enablePan = _settings.getSetting('enablePan');
      _orbitControls.panSpeed = _settings.getSetting('panSpeed');

      _orbitControls.enableKeyPan = _settings.getSetting('enableKeyPan');
      _orbitControls.keyPanSpeed = _settings.getSetting('keyPanSpeed') * 7;

      let t = _settings.getSetting('defaults.perspective.target');
      _orbitControls.target.set(t.x, t.y, t.z);

      _settings.registerHook('fov', _fovHook);
      _settings.registerHook('defaults.perspective', _perspectiveHook);
      _settings.registerHook('defaults', _defaultsHook);
      _settings.registerHook('enableOrbitControls', _enableOrbitControlsHook);
      _settings.registerHook('damping', _dampingHook);
      _settings.registerHook('enableRotation', _enableRotationHook);
      _settings.registerHook('rotationSpeed', _rotationSpeedHook);
      _settings.registerHook('enableAutoRotation', _enableAutoRotationHook);
      _settings.registerHook('autoRotationSpeed', _autoRotationSpeedHook);
      _settings.registerHook('enableZoom', _enableZoomHook);
      _settings.registerHook('zoomSpeed', _zoomSpeedHook);
      _settings.registerHook('enablePan', _enablePanHook);
      _settings.registerHook('panSpeed', _panSpeedHook);
      _settings.registerHook('enableKeyPan', _enableKeyPanHook);
      _settings.registerHook('keyPanSpeed', _keyPanSpeedHook);
      _settings.registerHook('restrictions.position.cube', _restrictionsPositionCubeHook);
      _settings.registerHook('restrictions.position.cube.min', value => {
        let s = _settings.getSetting('restrictions.position.cube');
        s.min = value;
        return _restrictionsPositionCubeHook(s);
      });
      _settings.registerHook('restrictions.position.cube.max', value => {
        let s = _settings.getSetting('restrictions.position.cube');
        s.max = value;
        return _restrictionsPositionCubeHook(s);
      });
      _settings.registerHook('restrictions.position.sphere', _restrictionsPositionSphereHook);
      _settings.registerHook('restrictions.position.sphere.center', value => {
        let s = _settings.getSetting('restrictions.position.sphere');
        s.center = value;
        return _restrictionsPositionSphereHook(s);
      });
      _settings.registerHook('restrictions.position.sphere.radius', value => {
        let s = _settings.getSetting('restrictions.position.sphere');
        s.radius = value;
        return _restrictionsPositionSphereHook(s);
      });
      _settings.registerHook('restrictions.target.cube', _restrictionsTargetCubeHook);
      _settings.registerHook('restrictions.target.cube.min', value => {
        let s = _settings.getSetting('restrictions.target.cube');
        s.min = value;
        return _restrictionsTargetCubeHook(s);
      });
      _settings.registerHook('restrictions.target.cube.max', value => {
        let s = _settings.getSetting('restrictions.target.cube');
        s.max = value;
        return _restrictionsTargetCubeHook(s);
      });
      _settings.registerHook('restrictions.target.sphere', _restrictionsTargetSphereHook);
      _settings.registerHook('restrictions.target.sphere.center', value => {
        let s = _settings.getSetting('restrictions.target.sphere');
        s.center = value;
        return _restrictionsTargetSphereHook(s);
      });
      _settings.registerHook('restrictions.target.sphere.radius', value => {
        let s = _settings.getSetting('restrictions.target.sphere');
        s.radius = value;
        return _restrictionsTargetSphereHook(s);
      });
      _settings.registerHook('restrictions.rotation', _restrictionsRotationHook);
      _settings.registerHook('restrictions.rotation.minPolarAngle', value => {
        let s = _settings.getSetting('restrictions.rotation');
        s.minPolarAngle = value;
        return _restrictionsRotationHook(s);
      });
      _settings.registerHook('restrictions.rotation.maxPolarAngle', value => {
        let s = _settings.getSetting('restrictions.rotation');
        s.maxPolarAngle = value;
        return _restrictionsRotationHook(s);
      });
      _settings.registerHook(
        'restrictions.rotation.minAzimuthAngle',
        value => {
          let s = _settings.getSetting('restrictions.rotation');
          s.minAzimuthAngle = value;
          return _restrictionsRotationHook(s);
        }
      );
      _settings.registerHook(
        'restrictions.rotation.maxAzimuthAngle',
        value => {
          let s = _settings.getSetting('restrictions.rotation');
          s.maxAzimuthAngle = value;
          return _restrictionsRotationHook(s);
        }
      );
      _settings.registerHook('restrictions.zoom', _restrictionsZoomHook);
      _settings.registerHook('restrictions.zoom.minDistance', value => {
        let s = _settings.getSetting('restrictions.zoom');
        s.minDistance = value;
        return _restrictionsZoomHook(s);
      });
      _settings.registerHook('restrictions.zoom.maxDistance', value => {
        let s = _settings.getSetting('restrictions.zoom');
        s.maxDistance = value;
        return _restrictionsZoomHook(s);
      });
      _settings.registerNotifier('zoomExtentsFactor', _zoomExtentsFactorNotifier);
    }

    _onTouchesBegan(event) {
      _orbitControlsProxy.onTouchesBegan(event);
    }

    _onTouchesMoved(event) {
      _orbitControlsProxy.onTouchesMoved(event);
    }

    _onTouchesEnded(event) {
      _orbitControlsProxy.onTouchesEnded(event);
    }

    ////////////
    ////////////
    //
    // CameraHandler API
    //
    ////////////
    ////////////
        
    /** @inheritdoc */
    pause() {
      _active = false;
      _orbitControls.enabled = false;
      _orbitControls.resetState();
    }

    /** @inheritdoc */
    resume() { 
      _active = true;
      _orbitControls.enabled = true;
    }

    /** @inheritdoc */
    destroy() { 
      // destroy the camera handler
    }




    /** @inheritdoc */
    setPosition(position) {
      // TODO, restrictions testing
      if (!GLOBAL_UTILS.typeCheck(position, 'vector3obj', _handlers.threeDManager.warn, 'CameraHandler.setPosition')) return false;

      _orbitControls.object.position.set(position.x, position.y, position.z);
      return true;
    }

    /** @inheritdoc */
    resetPosition() {
      return that.setPosition(_settings.getSetting('defaults.perspective.position'));
    }

    /** @inheritdoc */
    getPosition() {
      return GLOBAL_UTILS.deepCopy(_orbitControls.object.position);
    }




    /** @inheritdoc */
    setTarget(target) {
      // TODO, restrictions testing
      if (!GLOBAL_UTILS.typeCheck(target, 'vector3obj', _handlers.threeDManager.warn, 'CameraHandler.setTarget')) return false;

      _orbitControls.target.set(target.x, target.y, target.z);
      return true;
    }

    /** @inheritdoc */
    resetTarget() {
      return that.setTarget(_settings.getSetting('defaults.perspective.target'));
    }

    /** @inheritdoc */
    getTarget() {
      return GLOBAL_UTILS.deepCopy(_orbitControls.target);
    }




    /** @inheritdoc */
    setPositionAndTarget(position, target, options) {
      // TODO, restrictions testing
      let scope = 'CameraHandler.setPositionAndTarget';

      if (!(GLOBAL_UTILS.typeCheck(position, 'vector3any', _handlers.threeDManager.warn, scope) &&
        GLOBAL_UTILS.typeCheck(target, 'vector3any', _handlers.threeDManager.warn, scope))) 
        return Promise.resolve(false);

      _orbitControls.object.position.copy(GLOBAL_UTILS.toVector3(position));
      _orbitControls.target.copy(GLOBAL_UTILS.toVector3(target));

      _handlers.renderingHandler.renderHidden();
      return Promise.resolve(true);
    }

    /** @inheritdoc */
    resetPositionAndTarget(options) {
      return that.setPositionAndTarget(_settings.getSetting('defaults.perspective.position'), _settings.getSetting('defaults.perspective.target'), options);
    }

    /** @inheritdoc */
    getPositionAndTarget() {
      return { position: that.getPosition(), target: that.getTarget() };
    }




    /** @inheritdoc */
    zoomExtents(options, bb) {
      if (!(bb && bb instanceof THREE.Box3)) {
        bb = _geometryNode.computeSceneBoundingBox();

        if (bb.min.distanceTo(bb.max) == 0) {
          let mv = Number.MIN_VALUE;
          bb = new THREE.Box3(new THREE.Vector3(-mv, -mv, -mv), new THREE.Vector3(mv, mv, mv));
        }
      }

      let bbCenter = new THREE.Vector3();
      bb.getCenter(bbCenter);

      // correct with factor
      if (_settings.hasSetting('zoomExtentsFactor')) {
        let tmpDir = new THREE.Vector3();
        tmpDir.subVectors(bb.max, bbCenter);
        tmpDir.multiplyScalar(_settings.getSetting('zoomExtentsFactor'));
        bb = new THREE.Box3(new THREE.Vector3().subVectors(bbCenter, tmpDir), new THREE.Vector3().addVectors(bbCenter, tmpDir));
      }

      // test points
      let testPt = [];
      testPt.push(bb.min);
      testPt.push(bb.max);
      testPt.push(new THREE.Vector3(bb.max.x, bb.min.y, bb.min.z));
      testPt.push(new THREE.Vector3(bb.max.x, bb.max.y, bb.min.z));
      testPt.push(new THREE.Vector3(bb.max.x, bb.min.y, bb.max.z));
      testPt.push(new THREE.Vector3(bb.min.x, bb.max.y, bb.max.z));
      testPt.push(new THREE.Vector3(bb.min.x, bb.min.y, bb.max.z));
      testPt.push(new THREE.Vector3(bb.min.x, bb.max.y, bb.min.z));

      let offsetFromCenter = 0.0;

      // see https://threejs.org/docs/#api/en/cameras/Camera.getWorldDirection
      let cDir = new THREE.Vector3();
      _orbitControls.object.getWorldDirection(cDir);
      cDir.normalize();

      let cDirLine = new THREE.Line3(bbCenter.clone().addScaledVector(cDir, -100), bbCenter.clone());
      let cX = new THREE.Vector3();
      cX.crossVectors(cDir, _orbitControls.object.up);
      cX.normalize();
      let cY = new THREE.Vector3();
      cY.crossVectors(cX, cDir);
      cY.normalize();

      for (let i = 0, bound = testPt.length; i < bound; i++) {
        let testPtProjected = new THREE.Vector3();
        cDirLine.closestPointToPoint(testPt[i], false, testPtProjected);
        let lineNormal = testPt[i].clone();
        lineNormal.sub(testPtProjected);

        let point2DX = lineNormal.dot(cX);
        let point2DY = lineNormal.dot(cY);

        // see https://threejs.org/docs/#api/en/cameras/PerspectiveCamera.fov
        // and https://threejs.org/docs/#api/en/cameras/PerspectiveCamera.aspect
        // TODO Alex to Michael: not clear to me why we subtract 2 (degrees) before computing the tan, do you know why?
        let neededDistCameraToTestPtProjected = Math.max(Math.abs(point2DX / Math.tan((0.5 * _orbitControls.object.fov * _orbitControls.object.aspect - 2) * (Math.PI / 180))), Math.abs(point2DY / Math.tan((0.5 * _orbitControls.object.fov - 2) * (Math.PI / 180))));

        let centerToProjectedTestPoint = testPtProjected
          .clone()
          .sub(bbCenter);
        let distCenterToProjectedTestPt = centerToProjectedTestPoint.dot(cDir);

        offsetFromCenter = Math.max(offsetFromCenter, neededDistCameraToTestPtProjected - distCenterToProjectedTestPt);
      }

      cDir.multiplyScalar(-1 * offsetFromCenter);
      let newPos = new THREE.Vector3();
      newPos.addVectors(bbCenter, cDir);

      // actually set camera
      return that.setPositionAndTarget(newPos, bbCenter, options);
    }

    /** @inheritdoc */
    setCameraPath(positions, targets, options) {
      _handlers.threeDManager.debug('This method is not implemented in this viewport.');
    }




    /** @inheritdoc */
    getCamera() {
      return _camera;
    }

    /** @inheritdoc */
    isCameraMoving() {
      return _orbitControls.isMoving;
    }

    /** @inheritdoc */
    updateOrbitControls() {
      _orbitControls.update();
    }

    /** @inheritdoc */
    adjustToBoundingSphere(bs) {
      _camera.far = 100.0 * bs.radius;
      _camera.near = 0.01 * bs.radius;
      _camera.updateProjectionMatrix();
    }

    /** @inheritdoc */
    onResize(width, height) {
      _orbitControlsProxy.setClientSize(width, height);
      _camera.aspect = width / height;
      _camera.setViewOffset(width, height, 0, 0, width, height);
      _camera.updateProjectionMatrix();
    }
  }

  return new CameraHandler();
};

module.exports = CameraHandler;
